/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Saved List State Hook
 *
 * Headless hook that wraps useListPageState + useSavedViewsUnified + URL sync.
 * Provides unified state management for list pages with saved views support.
 */

"use client";

import type { NormalizedSavedView } from "@/types/saved-views";
import { useCallback, useMemo, useRef } from "react";
import type { z } from "zod";
import type { SavedViewType } from "../saved-views/types";
import { useSavedViewsUnified } from "../saved-views/use-saved-views-unified";
import { useListPageState } from "../tables/use-list-page-state";

type FilterRecord = Record<string, unknown>;

export interface UseSavedListStateConfig<
  TFilter extends FilterRecord = FilterRecord,
> {
  /**
   * Stable identifier for the filter surface (e.g., "cost-profiles", "suppliers")
   */
  id: string;

  /**
   * Zod schema for validating filter types
   */
  filterSchema?: z.ZodSchema<TFilter>;

  /**
   * URL keys for syncing state (search, page, pageSize)
   */
  urlKeys?: {
    search?: string;
    page?: string;
    pageSize?: string;
  };

  /**
   * URL keys for syncing filter state
   */
  filterUrlKeys?: Partial<Record<keyof TFilter & string, string>>;

  /**
   * Page size for pagination
   */
  pageSize?: number;

  /**
   * Search placeholder text
   */
  searchPlaceholder: string;

  /**
   * Whether user can create new items
   */
  canCreate?: boolean;

  /**
   * Handler for creating new items
   */
  onCreateNew?: () => void;

  /**
   * View type for saved views (e.g., "cost-profiles", "suppliers")
   */
  viewType: string;

  /**
   * Storage key for local storage view types (required for non-server-backed types)
   */
  storageKey?: string;

  /**
   * Initial filter state
   */
  initialFilters?: Partial<TFilter>;

  /**
   * Default filters for reset
   */
  defaultFilters?: TFilter;

  /**
   * Whether to sync with URL
   */
  syncWithUrl?: boolean;
}

export interface UseSavedListStateReturn<
  TFilter extends FilterRecord = FilterRecord,
> {
  search: {
    searchInputRef: React.RefObject<HTMLInputElement | null>;
    searchProps: {
      placeholder: string;
      value: string;
      onChange: (value: string) => void;
      onClear?: () => void;
      testId?: string;
    };
    searchTermInput: string;
    setSearchTermInput: (value: string) => void;
    searchTermServer: string;
  };

  pagination: {
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
  };

  filters: {
    filterState: TFilter;
    setFilterState: (filters: Partial<TFilter>) => void;
    handleClearAllFilters: () => void;
  };

  savedViews: {
    views: NormalizedSavedView<TFilter>[];
    selectedViewId: string | undefined;
    isDirty: boolean;
    handleApplyView: (viewId: string | null) => void;
    handleSaveView: () => Promise<void>;
    handleDeleteView: () => Promise<void>;
  };

  create: {
    canCreate: boolean;
    onCreate: () => void;
  };
}

export function useSavedListState<TFilter extends FilterRecord = FilterRecord>(
  config: UseSavedListStateConfig<TFilter>,
): UseSavedListStateReturn<TFilter> {
  const {
    id,
    filterSchema,
    urlKeys,
    filterUrlKeys,
    pageSize = 25,
    searchPlaceholder,
    canCreate = false,
    onCreateNew,
    viewType,
    storageKey,
    initialFilters,
    defaultFilters,
    syncWithUrl = true,
  } = config;

  const listPageState = useListPageState<TFilter>({
    id,
    initialFilters,
    pageSize,
    syncWithUrl,
    urlKeys,
    filterUrlKeys,
    searchPlaceholder,
    canCreate,
    onCreateNew,
    testIdPrefix: id,
  });

  const prevFilterKeyRef = useRef<string>("");
  const prevFiltersRef = useRef<TFilter & { search?: string }>(undefined);

  const currentFilters = useMemo<TFilter & { search?: string }>(() => {
    const {
      search: _search,
      page: _page,
      pageSize: _pageSize,
      ...filters
    } = listPageState.filterState;
    const next = {
      ...filters,
      search: listPageState.searchTermServer || undefined,
    } as TFilter & { search?: string };

    const key = JSON.stringify(next);
    if (key === prevFilterKeyRef.current && prevFiltersRef.current) {
      return prevFiltersRef.current;
    }
    prevFilterKeyRef.current = key;
    prevFiltersRef.current = next;
    return next;
  }, [listPageState.filterState, listPageState.searchTermServer]);

  const handleApplyView = useCallback(
    (filters: TFilter & { search?: string }) => {
      const baseFilters =
        (defaultFilters as (TFilter & { search?: string }) | undefined) ??
        ({} as TFilter & { search?: string });
      let normalizedFilters = {
        ...baseFilters,
        ...filters,
      };

      if (filterSchema) {
        const result = filterSchema.safeParse(normalizedFilters);
        if (!result.success) {
          return;
        }
        normalizedFilters = result.data as TFilter & { search?: string };
      }

      const { search: viewSearch, ...viewFilters } = normalizedFilters;
      listPageState.setFilterState(
        viewFilters as Partial<typeof listPageState.filterState>,
      );
      if (viewSearch !== undefined) {
        listPageState.setSearchTermInput(viewSearch);
      } else {
        listPageState.setSearchTermInput("");
      }
    },
    [defaultFilters, filterSchema, listPageState],
  );

  const savedViews = useSavedViewsUnified<TFilter & { search?: string }>({
    viewType: viewType as SavedViewType,
    storageKey,
    currentFilters,
    onApplyView: handleApplyView,
    defaultFilters: defaultFilters as TFilter & { search?: string },
    syncWithUrl,
  });

  const handleClearAll = useCallback(() => {
    listPageState.handleClearAllFilters();
    listPageState.setSearchTermInput("");
  }, [listPageState]);

  return {
    search: {
      searchInputRef: listPageState.searchInputRef,
      searchProps: listPageState.searchProps,
      searchTermInput: listPageState.searchTermInput,
      setSearchTermInput: listPageState.setSearchTermInput,
      searchTermServer: listPageState.searchTermServer,
    },
    pagination: {
      page: listPageState.page,
      setPage: listPageState.setPage,
      pageSize: listPageState.pageSize,
      setPageSize: listPageState.setPageSize,
    },
    filters: {
      filterState: listPageState.filterState as TFilter,
      setFilterState: (filters: Partial<TFilter>) => {
        listPageState.setFilterState(
          filters as Partial<typeof listPageState.filterState>,
        );
      },
      handleClearAllFilters: handleClearAll,
    },
    savedViews: {
      views: savedViews.views,
      selectedViewId: savedViews.selectedViewId,
      isDirty: savedViews.isDirty,
      handleApplyView: savedViews.handleApplyView,
      handleSaveView: savedViews.handleSaveView,
      handleDeleteView: savedViews.handleDeleteView,
    },
    create: {
      canCreate,
      onCreate: onCreateNew ?? (() => {}),
    },
  };
}
